/**
 * ORA - Chat Messages Hook
 * Manages chat messages and conversation logging for AI Agent
 */

import { useState } from "react";

export interface Message {
  role: "user" | "assistant";
  content: string;
  id?: string;
  timestamp?: number;
  feedback?: "thumbs_up" | "thumbs_down" | null;
}

export function useChatMessages() {
  const [messages, setMessages] = useState<Message[]>([]);

  // Add a new message to the conversation
  const addMessage = (message: Message) => {
    setMessages(prev => [...prev, message]);
  };

  // Update an existing message (e.g., for feedback)
  const updateMessage = (messageId: string, updates: Partial<Message>) => {
    setMessages(prev => 
      prev.map(msg => 
        msg.id === messageId ? { ...msg, ...updates } : msg
      )
    );
  };

  // Clear all messages
  const clearMessages = () => {
    setMessages([]);
  };

  // Log conversation to localStorage for analytics
  const logConversation = (
    userQuestion: string, 
    aiResponse: string, 
    pillButtonContext?: string
  ): string => {
    const stored = localStorage.getItem("conversationLogs");
    const logs = stored ? JSON.parse(stored) : [];
    
    const conversationLog = {
      id: `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      sessionId: `session_${Date.now()}`,
      userId: "user_demo", // In production, use actual user ID
      userTier: "free", // In production, get from user profile
      timestamp: Date.now(),
      userQuestion,
      aiResponse,
      pillButtonContext,
      tokensUsed: Math.ceil((userQuestion.length + aiResponse.length) / 4), // Rough estimate
      conversationLength: messages.length + 1,
      dataConsent: localStorage.getItem("dataConsent") === "true",
      feedback: null
    };
    
    logs.push(conversationLog);
    localStorage.setItem("conversationLogs", JSON.stringify(logs));
    
    return conversationLog.id;
  };

  // Handle feedback (thumbs up/down)
  const handleFeedback = (messageId: string, feedbackType: "thumbs_up" | "thumbs_down") => {
    // Update message feedback in state
    updateMessage(messageId, { feedback: feedbackType });

    // Update conversation log in localStorage
    const stored = localStorage.getItem("conversationLogs");
    if (stored) {
      const logs = JSON.parse(stored);
      const updatedLogs = logs.map((log: any) => {
        if (log.id === messageId) {
          return { ...log, feedback: feedbackType };
        }
        return log;
      });
      localStorage.setItem("conversationLogs", JSON.stringify(updatedLogs));
    }
  };

  return {
    messages,
    addMessage,
    updateMessage,
    clearMessages,
    logConversation,
    handleFeedback,
  };
}
